import { FC } from "react"

import { PointRule } from "types/tournament.types"

import { NumberInput } from "./NumberInput"

interface ConditionFieldsProps {
  condition: PointRule["if"]
  onChange: (key: string, val: any) => void
}

export const ConditionFields: FC<ConditionFieldsProps> = ({ condition, onChange }) => {
  switch (condition.type) {
    case "win_streak":
    case "loss_streak":
      return <NumberInput label="Streak length" value={condition.count} onChange={(v) => onChange("count", v)} placeholder="e.g., 3" />

    case "win_x_of_y":
      return (
        <div className="grid grid-cols-2 gap-3">
          <NumberInput label="Wins required" value={condition.wins} onChange={(v) => onChange("wins", v)} placeholder="e.g., 5" />
          <NumberInput label="Out of last N spins" value={condition.spins} onChange={(v) => onChange("spins", v)} placeholder="e.g., 10" />
        </div>
      )

    case "multiplier_gte":
    case "multiplier_lte":
    case "multiplier_exact":
      return (
        <NumberInput
          label="Multiplier value"
          value={condition.value}
          onChange={(v) => onChange("value", v)}
          step={0.01}
          placeholder="e.g., 2.0"
        />
      )

    case "multiplier_between":
      return (
        <div className="grid grid-cols-2 gap-3">
          <NumberInput
            label="Min multiplier"
            value={condition.min}
            onChange={(v) => onChange("min", v)}
            step={0.01}
            placeholder="e.g., 1.0"
          />
          <NumberInput
            label="Max multiplier"
            value={condition.max}
            onChange={(v) => onChange("max", v)}
            step={0.01}
            placeholder="e.g., 10.0"
          />
        </div>
      )

    case "bet_gte":
    case "bet_lte":
    case "bet_lt":
      return (
        <NumberInput label="Bet amount (cents)" value={condition.amount} onChange={(v) => onChange("amount", v)} placeholder="e.g., 100" />
      )

    case "win_gte":
    case "win_lte":
      return (
        <NumberInput label="Win amount (cents)" value={condition.amount} onChange={(v) => onChange("amount", v)} placeholder="e.g., 500" />
      )

    case "total_wagers_gte":
      return (
        <NumberInput
          label="Total wagers threshold (cents)"
          value={condition.amount}
          onChange={(v) => onChange("amount", v)}
          placeholder="e.g., 10000"
        />
      )

    case "total_wins_gte":
    case "spin_count_gte":
      return <NumberInput label="Count" value={condition.count} onChange={(v) => onChange("count", v)} placeholder="e.g., 50" />

    case "spins_in_timeframe":
      return (
        <div className="grid grid-cols-2 gap-3">
          <NumberInput label="Spins required" value={condition.spins} onChange={(v) => onChange("spins", v)} placeholder="e.g., 10" />
          <NumberInput label="Within minutes" value={condition.minutes} onChange={(v) => onChange("minutes", v)} placeholder="e.g., 5" />
        </div>
      )

    case "inactivity":
    case "first_minutes":
    case "last_minutes":
      return <NumberInput label="Minutes" value={condition.minutes} onChange={(v) => onChange("minutes", v)} placeholder="e.g., 30" />

    case "time_window":
      return (
        <div className="grid grid-cols-2 gap-3">
          <NumberInput
            label="Start hour (0-23)"
            value={condition.hour_start}
            onChange={(v) => onChange("hour_start", v)}
            min={0}
            max={23}
            placeholder="e.g., 18"
          />
          <NumberInput
            label="End hour (0-23)"
            value={condition.hour_end}
            onChange={(v) => onChange("hour_end", v)}
            min={0}
            max={23}
            placeholder="e.g., 22"
          />
        </div>
      )

    case "reached_rank":
      return (
        <NumberInput label="Rank (1 = first place)" value={condition.rank} onChange={(v) => onChange("rank", v)} placeholder="e.g., 10" />
      )

    case "playing_game":
      return (
        <div>
          <label className="text-xs text-gray-500 dark:text-gray-400">Game IDs (comma-separated)</label>
          <input
            type="text"
            value={(condition.game_ids || []).join(", ")}
            onChange={(e) =>
              onChange(
                "game_ids",
                e.target.value
                  .split(",")
                  .map((s: string) => s.trim())
                  .filter(Boolean)
              )
            }
            className="w-full px-3 py-2 border rounded dark:bg-gray-800 dark:border-gray-700 dark:text-white"
            placeholder="e.g., softswiss:1234, softswiss:5678"
          />
        </div>
      )

    case "playing_category":
      return (
        <div>
          <label className="text-xs text-gray-500 dark:text-gray-400">Game provider / category</label>
          <input
            type="text"
            value={condition.category || ""}
            onChange={(e) => onChange("category", e.target.value)}
            className="w-full px-3 py-2 border rounded dark:bg-gray-800 dark:border-gray-700 dark:text-white"
            placeholder="e.g., softswiss, originals"
          />
        </div>
      )

    default:
      return null
  }
}
